import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  LinearProgress,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useLocation } from 'react-router-dom';
import { PlaceAutocompleteTextField, PlaceResult } from '../../features/location/PlaceAutocompleteTextField';
import { useImageUpload } from '../../hooks/useImageUpload';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://diaperstops-backend.fly.dev';

interface IncomingState {
  place?: PlaceResult;
}

const LocationDisclosureV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const incomingState = location.state as IncomingState | null;

  // Auth
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    setIsLoggedIn(true);
    setAuthChecked(true);
  }, [navigate]);

  // Form state
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(
    incomingState?.place || null
  );
  const [locationType, setLocationType] = useState('public_facility');
  const [isAccessible, setIsAccessible] = useState(true);
  const [isGenderNeutral, setIsGenderNeutral] = useState(true);
  const [notes, setNotes] = useState('');

  // Image upload
  const { uploading: imageUploading, progress: imageProgress, uploadImage } = useImageUpload();
  const [primaryFile, setPrimaryFile] = useState<File | null>(null);
  const [primaryPreview, setPrimaryPreview] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pre-fill from navigation state
  useEffect(() => {
    if (incomingState?.place) {
      setSelectedPlace(incomingState.place);
    }
  }, []); // eslint-disable-line

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }
    
    setPrimaryFile(file);
    setPrimaryPreview(URL.createObjectURL(file));
    setUploadedImageUrl(null); // Reset uploaded URL
  };

  const removePrimaryPhoto = () => {
    setPrimaryFile(null);
    if (primaryPreview) URL.revokeObjectURL(primaryPreview);
    setPrimaryPreview(null);
    setUploadedImageUrl(null);
  };

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleSubmit = async () => {
    if (!selectedPlace || !selectedPlace.lat || !selectedPlace.lng) {
      setError('Please select a valid location with coordinates');
      return;
    }

    setSubmitting(true);
    setError(null);
    setProgress(10);

    try {
      const token = localStorage.getItem('token');

      // Step 1: Upload image first if provided
      let imageUrl: string | null = null;
      if (primaryFile && !uploadedImageUrl) {
        setProgress(15);
        const uploadResult = await uploadImage(primaryFile, 'primary');
        
        if (!uploadResult.success) {
          console.warn('Image upload failed:', uploadResult.error);
          // Continue without image
        } else {
          imageUrl = uploadResult.publicUrl || null;
          setUploadedImageUrl(imageUrl);
        }
        setProgress(40);
      } else if (uploadedImageUrl) {
        imageUrl = uploadedImageUrl;
        setProgress(40);
      }

      // Step 2: Create location
      const locationPayload = {
        name: selectedPlace.name || selectedPlace.address,
        address: selectedPlace.address,
        latitude: selectedPlace.lat,
        longitude: selectedPlace.lng,
        type: locationType,
        ...(imageUrl ? { imageUrl } : {}),
      };

      const locRes = await fetch(`${API_BASE_URL}/api/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(locationPayload),
      });

      if (!locRes.ok) {
        const errorData = await locRes.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to create location');
      }

      const locData = await locRes.json();
      const locationId = locData.id || locData.locationId;
      
      if (!locationId) {
        throw new Error('No location ID returned from server');
      }

      setProgress(70);

      // Step 3: Create station
      const stationPayload: Record<string, any> = {
        locationId,
        isAccessible,
        isGenderNeutral,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(imageUrl ? { imageUrl } : {}),
      };

      const stRes = await fetch(`${API_BASE_URL}/api/stations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(stationPayload),
      });

      if (!stRes.ok) {
        const errorData = await stRes.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to create station');
      }

      const stData = await stRes.json();
      const stationId = stData.id || stData.stationId;

      setProgress(100);
      setSuccess(true);

      // Navigate to success page
      setTimeout(() => {
        navigate('/spot-submitted', {
          state: {
            stationId,
            locationId,
            address: selectedPlace.address,
            name: selectedPlace.name || selectedPlace.address,
            lat: selectedPlace.lat,
            lng: selectedPlace.lng,
            locationType,
            primaryImageUrl: imageUrl,
            isAccessible,
            isGenderNeutral,
            notes: notes.trim() || undefined,
          },
        });
      }, 800);

    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authChecked) return null;

  const overallProgress = imageUploading 
    ? (imageProgress?.percentage || 0) * 0.3 
    : progress;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#E94057' }}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate('/home')}
            sx={{ mr: 2, color: '#fff' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: 'transparent', width: 32, height: 32, mr: 1 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.5 2 5.5 4.5 5.5 8c0 1.5.5 2.9 1.4 4l-2.9 7h3l1-2.5h6l1 2.5h3l-2.9-7c.9-1.1 1.4-2.5 1.4-4 0-3.5-3-6-6.5-6zm-3 12.5l1-2.5h4l1 2.5H9zm3-5.5c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z"/>
            </svg>
          </Avatar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: '#fff' }}>
            Post a Location
          </Typography>
          {isLoggedIn && (
            <Button
              color="inherit"
              onClick={() => {
                localStorage.removeItem('token');
                navigate('/login');
              }}
              sx={{ color: '#fff' }}
            >
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Progress bar */}
      {(submitting || imageUploading) && (
        <LinearProgress 
          variant="determinate" 
          value={overallProgress} 
          sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#E94057' } }} 
        />
      )}

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {/* Header */}
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: '#333' }}>
          📍 Add a Baby-Friendly Spot
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#888' }}>
          Share a location with diaper changing facilities, nursing rooms, or family-friendly amenities.
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
            ✅ Location created! Redirecting...
          </Alert>
        )}

        {/* Location Search */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          {selectedPlace ? (
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ flex: 1, mr: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#E94057', lineHeight: 1.3 }}>
                    {selectedPlace.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mt: 0.3 }}>
                    {selectedPlace.address}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                    📍 {selectedPlace.lat?.toFixed(6)}, {selectedPlace.lng?.toFixed(6)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSelectedPlace(null)}
                  sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#eee' } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
              Search for the location
            </Typography>
          )}

          <Box sx={{ opacity: selectedPlace ? 0.45 : 1, pointerEvents: selectedPlace ? 'none' : 'auto' }}>
            <PlaceAutocompleteTextField
              onPlaceSelect={(p: PlaceResult) => {
                setSelectedPlace(p);
                setError(null);
              }}
              placeholder="Enter address or place name..."
            />
          </Box>

          {selectedPlace && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#aaa' }}>
              Wrong place? Click the ✕ above to search again.
            </Typography>
          )}
        </Paper>

        {/* Location Type & Options */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            What kind of place is this?
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Location Type</InputLabel>
            <Select
              value={locationType}
              label="Location Type"
              onChange={(e) => setLocationType(e.target.value)}
            >
              <MenuItem value="public_facility">Public Facility</MenuItem>
              <MenuItem value="mall">Shopping Mall</MenuItem>
              <MenuItem value="restaurant">Restaurant</MenuItem>
              <MenuItem value="park">Park</MenuItem>
              <MenuItem value="hospital">Hospital / Medical</MenuItem>
              <MenuItem value="airport">Airport</MenuItem>
              <MenuItem value="gas_station">Gas Station</MenuItem>
              <MenuItem value="store">Retail Store</MenuItem>
              <MenuItem value="library">Library</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Chip
              label="♿ Accessible"
              variant={isAccessible ? 'filled' : 'outlined'}
              color={isAccessible ? 'success' : 'default'}
              onClick={() => setIsAccessible(!isAccessible)}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label="🚻 Gender Neutral"
              variant={isGenderNeutral ? 'filled' : 'outlined'}
              color={isGenderNeutral ? 'info' : 'default'}
              onClick={() => setIsGenderNeutral(!isGenderNeutral)}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Paper>

        {/* Primary Photo */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            📸 Primary Photo{' '}
            <Typography component="span" variant="caption" sx={{ color: '#aaa' }}>
              (optional)
            </Typography>
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#888' }}>
            Upload a photo of the changing station or facility. Max 10MB.
          </Typography>

          {!primaryPreview ? (
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCameraIcon />}
              disabled={submitting || imageUploading}
              sx={{
                borderColor: '#E94057',
                color: '#E94057',
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': { borderColor: '#d63649', bgcolor: 'rgba(233,64,87,.04)' },
              }}
            >
              Choose Photo
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handlePhotoSelect}
              />
            </Button>
          ) : (
            <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <img
                src={primaryPreview}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'cover',
                  borderRadius: 12,
                  border: '1px solid #E0E0E0',
                }}
              />
              {uploadedImageUrl && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Uploaded"
                  color="success"
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    left: 12,
                    fontWeight: 600,
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={removePrimaryPhoto}
                disabled={imageUploading}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,.6)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,.8)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {imageUploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={imageProgress?.percentage || 0}
                sx={{ borderRadius: 1, height: 6 }}
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#888' }}>
                Uploading... {Math.round(imageProgress?.percentage || 0)}%
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Notes */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            📝 Additional Notes{' '}
            <Typography component="span" variant="caption" sx={{ color: '#aaa' }}>
              (optional)
            </Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="e.g., Located on 2nd floor near food court, very clean, has changing table and sink..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText={`${notes.length}/500 characters`}
            size="small"
          />
        </Paper>

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={submitting || !selectedPlace || success || imageUploading}
          sx={{
            bgcolor: '#E94057',
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            fontSize: '1.1rem',
            '&:hover': { bgcolor: '#d63649' },
            '&:disabled': { bgcolor: '#ccc' },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
              Creating Location...
            </>
          ) : success ? (
            <>
              <CheckCircleIcon sx={{ mr: 1 }} />
              Created Successfully!
            </>
          ) : imageUploading ? (
            <>
              <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
              Uploading Image...
            </>
          ) : (
            'Create Location →'
          )}
        </Button>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, textAlign: 'center', color: '#aaa' }}>
          You can add more photos and details on the next page.
        </Typography>

        {/* Powered by Google */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <img
            src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png"
            alt="Powered by Google"
            style={{ height: 18, opacity: 0.7 }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default LocationDisclosureV2;
